/**
 * Blog Categories - Manage blog categories
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  HStack,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure
} from '@chakra-ui/react';
import {
  getBlogCategories,
  createBlogCategory,
  generateSlug,
  type BlogCategory
} from '../../services/blogService';

interface BlogCategoriesProps {
  onChange: () => void;
}

const BlogCategories = ({ onChange }: BlogCategoriesProps) => {
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    color: '#10B981'
  });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const PRESET_COLORS = [
    { name: 'Green', value: '#10B981' },
    { name: 'Blue', value: '#3B82F6' },
    { name: 'Purple', value: '#8B5CF6' },
    { name: 'Orange', value: '#F59E0B' },
    { name: 'Red', value: '#EF4444' },
    { name: 'Pink', value: '#EC4899' },
    { name: 'Teal', value: '#14B8A6' },
    { name: 'Indigo', value: '#6366F1' }
  ];

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await getBlogCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
      toast({
        title: 'Error',
        description: 'Failed to load categories',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNameChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      name: value,
      slug: generateSlug(value)
    }));
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Category name is required',
        status: 'error',
        duration: 2000,
      });
      return;
    }

    try {
      await createBlogCategory({
        name: formData.name.trim(),
        slug: formData.slug.trim(),
        description: formData.description.trim(),
        color: formData.color
      });

      toast({
        title: 'Success',
        description: 'Category created successfully',
        status: 'success',
        duration: 2000,
      });

      setFormData({ name: '', slug: '', description: '', color: '#10B981' });
      onClose();
      loadCategories();
      onChange();
    } catch (error) {
      console.error('Error creating category:', error);
      toast({
        title: 'Error',
        description: 'Failed to create category',
        status: 'error',
        duration: 3000,
      });
    }
  };

  return (
    <Box>
      <HStack mb={4}>
        <Button
          colorScheme="teal"
          onClick={onOpen}
        >
          + Add Category
        </Button>
      </HStack>

      <Box bg="white" borderRadius="lg" boxShadow="sm" borderWidth="1px" overflowX="auto">
        <Table variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th>Name</Th>
              <Th>Slug</Th>
              <Th>Description</Th>
              <Th>Color</Th>
              <Th>Posts</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={5} textAlign="center" py={4}>
                  Loading...
                </Td>
              </Tr>
            ) : categories.length === 0 ? (
              <Tr>
                <Td colSpan={5} textAlign="center" py={8} color="gray.500">
                  No categories yet. Create your first one!
                </Td>
              </Tr>
            ) : (
              categories.map((category) => (
                <Tr key={category.id} _hover={{ bg: 'gray.50' }}>
                  <Td fontWeight="medium">{category.name}</Td>
                  <Td fontSize="sm" color="gray.600">/{category.slug}</Td>
                  <Td fontSize="sm">{category.description || '-'}</Td>
                  <Td>
                    <Badge
                      px={3}
                      py={1}
                      borderRadius="full"
                      style={{ backgroundColor: category.color, color: 'white' }}
                    >
                      {category.color}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme="blue" variant="subtle">
                      {category.postCount || 0} posts
                    </Badge>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Create Category Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create New Category</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Category Name</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g., Car Care Tips"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Slug</FormLabel>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                  placeholder="Auto-generated from name"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional description"
                  rows={3}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Color</FormLabel>
                <HStack spacing={2} flexWrap="wrap">
                  {PRESET_COLORS.map(color => (
                    <Button
                      key={color.value}
                      size="sm"
                      onClick={() => setFormData(prev => ({ ...prev, color: color.value }))}
                      style={{ backgroundColor: color.value }}
                      color="white"
                      variant={formData.color === color.value ? 'solid' : 'outline'}
                      borderWidth={formData.color === color.value ? '2px' : '1px'}
                      borderColor="white"
                    >
                      {color.name}
                    </Button>
                  ))}
                </HStack>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="teal" onClick={handleCreate}>
              Create Category
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default BlogCategories;
